// Per-zone sensor live card — latest VWC/soil_temp/EC/leaf_wetness tiles per
// sensor + 24h sparkline for VWC. Renders an empty-state encouraging the
// admin to seed demo sensors if none exist for the zone.

import { useEffect, useState } from 'react';
import type { SensorReadingsSeries, SensorWithLatest } from '@greeniq/shared';
import { getSensorReadings, getZoneSensors } from '../api/sensors.js';

interface Props {
  courseSlug: string;
  zoneId: number;
}

export function SensorLiveCard({ courseSlug, zoneId }: Props) {
  const [sensors, setSensors] = useState<SensorWithLatest[] | null>(null);
  const [state, setState] = useState<{
    vwc2In: string | null;
    vwc4In: string | null;
    soilTempF: string | null;
    lastTickAt: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getZoneSensors(courseSlug, zoneId);
        if (!cancelled) {
          setSensors(r.sensors);
          setState(r.state);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug, zoneId]);

  if (error) {
    return (
      <section className="mt-8 p-5 bg-bg-card rounded-lg border border-border text-sm text-[color:var(--color-error)]">
        Sensor data unavailable: {error}
      </section>
    );
  }

  return (
    <section className="mt-8 p-5 bg-bg-card rounded-lg border border-border">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm uppercase tracking-wider text-fg-muted font-semibold">
          Soil sensors
        </h2>
        <span className="text-xs px-2 py-0.5 rounded-full bg-greeniq-500/10 text-greeniq-400 uppercase tracking-wider">
          Synthetic · FAO-56
        </span>
      </div>

      {sensors === null ? (
        <p className="mt-3 text-sm text-fg-muted">Loading…</p>
      ) : sensors.length === 0 ? (
        <div className="mt-3 text-sm text-fg-muted">
          No sensors placed on this zone yet. An AGEYE admin can seed demo sensors from the Soil
          Sensors page.
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {state && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StateTile label="VWC 2″" value={fmt(state.vwc2In, 3)} unit="" />
              <StateTile label="VWC 4″" value={fmt(state.vwc4In, 3)} unit="" />
              <StateTile label="Soil temp" value={fmt(state.soilTempF, 1)} unit="°F" />
              <StateTile
                label="Last tick"
                value={state.lastTickAt ? relTime(state.lastTickAt) : '—'}
                unit=""
              />
            </div>
          )}
          {sensors.map((s) => (
            <SensorRow key={s.sensor.id} entry={s} courseSlug={courseSlug} />
          ))}
        </div>
      )}
    </section>
  );
}

function SensorRow({ entry, courseSlug }: { entry: SensorWithLatest; courseSlug: string }) {
  const [series, setSeries] = useState<SensorReadingsSeries[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getSensorReadings(courseSlug, entry.sensor.id, '24h');
        if (!cancelled) setSeries(r.series);
      } catch {
        if (!cancelled) setSeries([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug, entry.sensor.id]);

  const vwcSeries = series?.find((s) => s.metric === 'vwc');
  const entries = Object.entries(entry.latest);

  return (
    <div className="p-3 rounded-md bg-bg-elevated">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm font-semibold text-greeniq-300">
          Sensor #{entry.sensor.id}
          <span className="ml-2 text-xs text-fg-muted font-normal">
            {entry.sensor.type === 'multi_probe'
              ? `${entry.sensor.depthInches ?? '?'}″ probe`
              : 'leaf wetness'}
          </span>
        </div>
        {vwcSeries && vwcSeries.points.length > 1 && (
          <Sparkline points={vwcSeries.points.map((p) => p.value)} />
        )}
      </div>
      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
        {entries.length === 0 && (
          <div className="text-xs text-fg-muted col-span-full">Awaiting first tick…</div>
        )}
        {entries.map(([metric, info]) => (
          <div key={metric}>
            <div className="text-[10px] uppercase tracking-wider text-fg-muted">
              {labelForMetric(metric)}
            </div>
            <div className="text-sm font-semibold text-fg">
              {fmtMetric(metric, info?.value)}
              {info?.unit ? <span className="text-[10px] text-fg-muted"> {info.unit}</span> : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Sparkline({ points }: { points: number[] }) {
  if (points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const width = 120;
  const height = 28;
  const step = width / (points.length - 1);
  const path = points
    .map((v, i) => {
      const x = i * step;
      const y = height - ((v - min) / range) * height;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={width} height={height} className="text-greeniq-600">
      <path d={path} fill="none" stroke="currentColor" strokeWidth={1.5} />
    </svg>
  );
}

function StateTile({ label, value, unit }: { label: string; value: string; unit: string }) {
  return (
    <div className="p-3 rounded-md bg-bg-elevated">
      <div className="text-xs uppercase tracking-wider text-fg-muted">{label}</div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-lg font-semibold text-greeniq-300">{value}</span>
        {unit && <span className="text-[10px] text-fg-muted">{unit}</span>}
      </div>
    </div>
  );
}

function labelForMetric(metric: string): string {
  switch (metric) {
    case 'vwc': return 'VWC';
    case 'soil_temp_f': return 'Soil temp';
    case 'ec_ds_m': return 'EC';
    case 'leaf_wetness': return 'Leaf wet';
    default: return metric;
  }
}

function fmtMetric(metric: string, value: number | undefined): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return '—';
  if (metric === 'vwc') return value.toFixed(3);
  if (metric === 'soil_temp_f') return value.toFixed(1);
  if (metric === 'ec_ds_m') return value.toFixed(2);
  if (metric === 'leaf_wetness') return value === 1 ? 'Wet' : 'Dry';
  return String(value);
}

function fmt(v: string | null, digits: number): string {
  if (v === null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(digits);
}

function relTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const diffS = Math.floor((Date.now() - then) / 1000);
  if (diffS < 60) return `${diffS}s ago`;
  if (diffS < 3600) return `${Math.floor(diffS / 60)}m ago`;
  if (diffS < 86400) return `${Math.floor(diffS / 3600)}h ago`;
  return `${Math.floor(diffS / 86400)}d ago`;
}
